import { Link } from "@tanstack/react-router";
import { Calendar, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { REGISTER_URL } from "@/components/site/data";
import {
  type EventItem,
  resolveEventImageUrl,
  getEventStatusRange,
  formatEventDateRange,
} from "@/lib/events";

interface EventsShowcaseProps {
  events: EventItem[];
  mounted?: boolean;
  title?: string;
  subtitle?: string;
  eyebrow?: string;
  onSelectEvent?: (event: EventItem) => void;
}

export function EventsShowcase({
  events,
  mounted = true,
  title = "Explore Our Events",
  subtitle = "Browse conferences and gatherings across the Arab capital markets.",
  eyebrow = "UASA Events Program",
  onSelectEvent,
}: EventsShowcaseProps) {
  return (
    <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-background via-secondary/30 to-background">
      <div className="max-w-7xl mx-auto">
        <div
          className={`text-center mb-10 sm:mb-12 ${mounted ? "animate-fade-in-up" : "opacity-0 translate-y-[20px]"}`}
        >
          <div className="inline-block px-4 py-1.5 rounded-full border border-accent/40 bg-accent/10 text-accent text-[10px] sm:text-xs uppercase tracking-[0.3em] mb-4">
            {eyebrow}
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-primary mb-3">
            {title}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-sm sm:text-base">
            {subtitle}
          </p>
        </div>

        {events.length === 0 ? (
          <Card className="p-12 text-center border-dashed">
            <p className="text-muted-foreground">No events to show.</p>
          </Card>
        ) : (
          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 ${mounted ? "animate-fade-in-up" : "opacity-0"}`}>
            {events.map((event) => {
              const status = getEventStatusRange(event);
              const imageUrl = resolveEventImageUrl(event.image_url);
              return (
                <Card
                  key={event.id}
                  className="overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-border/50"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img src={imageUrl} alt={event.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-3 left-3 flex gap-2">
                      {status !== "unknown" && (
                        <Badge
                          className={
                            status === "upcoming"
                              ? "bg-emerald-600/90 text-white border-0"
                              : "bg-black/50 text-white border-0"
                          }
                        >
                          {status === "upcoming" ? "Upcoming" : "Past"}
                        </Badge>
                      )}
                      {event.category && (
                        <Badge variant="secondary" className="bg-white/90 text-foreground">
                          {event.category}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="p-4 sm:p-5">
                    <h4 className="font-serif text-lg text-primary leading-snug mb-2 line-clamp-2">
                      {event.title}
                    </h4>
                    {(event.event_date_from || event.event_date) && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                        <Calendar className="w-3 h-3" /> {formatEventDateRange(event)}
                      </p>
                    )}
                    {event.location && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mb-4">
                        <MapPin className="w-3 h-3" /> {event.location}
                      </p>
                    )}
                    <div className="flex gap-2">
                      <Button asChild size="sm" variant="outline" className="flex-1">
                        <Link
                          to="/events/$eventId"
                          params={{ eventId: String(event.id) }}
                          onClick={() => onSelectEvent?.(event)}
                        >
                          View event
                        </Link>
                      </Button>
                      <Button asChild size="sm" className="flex-1 bg-accent hover:bg-accent/90 text-accent-foreground">
                        <a href={event.register_url || REGISTER_URL} target="_blank" rel="noreferrer">
                          Register
                        </a>
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
